// Page-setup editor (PRINT_TEMPLATE_PLAN.md §4.5).
//
// Edits the `PageConfig` slice: paper size (A4 / A5 / Letter),
// orientation (portrait / landscape), and four margin number inputs
// (5–30 mm). Each input is clamped on blur so a paste of garbage
// doesn't sneak through to the server.

import type { PageConfig } from '../../../../types/api';
import styles from './PageEditor.module.css';

const SIZES: ReadonlyArray<{ value: 'A4' | 'A5' | 'Letter'; label: string }> = [
  { value: 'A4', label: 'A4' },
  { value: 'A5', label: 'A5' },
  { value: 'Letter', label: 'Letter' },
];

const ORIENTATIONS: ReadonlyArray<{ value: 'portrait' | 'landscape'; label: string }> = [
  { value: 'portrait', label: 'Portrait' },
  { value: 'landscape', label: 'Landscape' },
];

// 3mm matches the lowest @page margin allowed by the schema (see
// printTemplates.types.ts pageMarginsSchema). The legacy receipt
// template ships at 3mm so the picker has to permit it.
const MARGIN_MIN = 3;
const MARGIN_MAX = 30;

export interface PageEditorProps {
  value: PageConfig;
  onChange: (next: PageConfig) => void;
}

export function PageEditor({ value, onChange }: PageEditorProps): JSX.Element {
  function setSize(size: 'A4' | 'A5' | 'Letter') {
    onChange({ ...value, size });
  }

  function setOrientation(orientation: 'portrait' | 'landscape') {
    onChange({ ...value, orientation });
  }

  function setMargin(side: 'top' | 'right' | 'bottom' | 'left', n: number) {
    const clamped = Math.min(MARGIN_MAX, Math.max(MARGIN_MIN, Math.round(n)));
    onChange({
      ...value,
      margins_mm: { ...value.margins_mm, [side]: clamped },
    });
  }

  return (
    <section className={styles.section} aria-label="Page setup">
      <header className={styles.head}>
        <h3 className={styles.title}>Page</h3>
      </header>

      <div className={styles.body}>
        <div className={styles.fieldRow}>
          <label className={styles.field}>
            <span>Page size</span>
            <select
              value={value.size}
              onChange={(e) => setSize(e.target.value as 'A4' | 'A5' | 'Letter')}
              className={styles.select}
            >
              {SIZES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>

          <fieldset className={styles.field}>
            <legend>Orientation</legend>
            <div className={styles.segmented}>
              {ORIENTATIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={
                    value.orientation === o.value
                      ? `${styles.segItem} ${styles.segItemActive}`
                      : styles.segItem
                  }
                  aria-pressed={value.orientation === o.value}
                  onClick={() => setOrientation(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </fieldset>
        </div>

        <div className={styles.marginGrid}>
          {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
            <label key={side} className={styles.marginField}>
              <span>{side[0]?.toUpperCase()}{side.slice(1)} margin (mm)</span>
              <input
                type="number"
                min={MARGIN_MIN}
                max={MARGIN_MAX}
                step={1}
                value={value.margins_mm[side]}
                onChange={(e) => setMargin(side, Number(e.target.value))}
                aria-label={`${side} margin (mm)`}
                className={styles.numberInput}
              />
            </label>
          ))}
        </div>
      </div>
    </section>
  );
}
